import { DisplayPrice } from '@models/vertical/displayPrice.model';

/**
 * Derive a `DisplayPrice` from the offer bids already stored on a document,
 * at render time.
 *
 * `/hh` verticals read a *stored* `displayPrice`, written monthly by the
 * dashboard job, because the price there has three consumers (page widget,
 * `<title>`, JSON-LD) and a render-time recompute in each would drift.
 * Mini-services need none of that: the API returns the whole document
 * unprojected (`getMiniVertical.ts` is a bare `findOne({ slug })`), so the raw
 * bids are already on the page — a median of ~47 of them — and the only
 * consumer is the meta block composed here. Storing a second copy would add a
 * job, a migration and a staleness window to buy nothing.
 *
 * The arithmetic is a deliberate mirror of the `live` origin in
 * `handyhand-dashboard/server/services/displayPrice/computeDisplayPrice.ts`:
 * same >2-stored-tasks gate, same q25/q50/q75 interpolation, same ceil-to-10,
 * same confidence tiers. A mini-service and the vertical above it must not
 * quote differently-derived numbers for the same work.
 *
 * Only the `live` origin is implemented. The job's `price-guide` fallback is
 * deliberately NOT reproduced: on this collection one guide is shared by up to
 * 1,091 mini-services (140 guides across 4,429 indexable pages), so its `min`
 * is a category band rather than a price for *this* service, and it sits above
 * the average of real bids on 22.5% of pages. The job already encodes that by
 * capping `price-guide` at `low` confidence, which `TITLE_CONFIDENCE` excludes.
 */

/** Shape of the stored task arrays. Only bids are read. */
export interface StoredTaskWithOffers {
  offers?: { priceBid?: number }[] | null;
}

export interface LivePriceSource {
  similarTasks?: StoredTaskWithOffers[] | null;
  taskExamples?: StoredTaskWithOffers[] | null;
}

const isUsable = (value?: number): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** Same interpolation as `SeoComponent.quantile`. Input must be sorted. */
function quantile(sorted: number[], q: number): number {
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

/** Same rounding as `SeoComponent.normalDist`. */
const ceil10 = (value: number): number => Math.ceil(value / 10) * 10;

/** Coefficient of variation — the dispersion input to the confidence tiers. */
export function coefficientOfVariation(values: number[]): number {
  if (values.length === 0) {
    return Infinity;
  }
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  if (mean === 0) {
    return Infinity;
  }
  const variance =
    values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance) / mean;
}

/**
 * Confidence per DISPLAY-PRICE-SPEC.md: `high` needs >=3 samples and CV < 0.6,
 * `medium` >=3 samples, `low` everything below.
 */
export function confidenceFor(
  sampleSize: number,
  cv: number,
): DisplayPrice['confidence'] {
  if (sampleSize >= 3 && cv < 0.6) {
    return 'high';
  }
  if (sampleSize >= 3) {
    return 'medium';
  }
  return 'low';
}

/**
 * `similarTasks` when more than two tasks are stored, else `taskExamples` on the
 * same rule. Below that the sample is one or two jobs and the tier would be
 * `low` anyway.
 *
 * NOTE: this is the OPPOSITE precedence to `processMiniVerticalData`, which
 * prefers `taskExamples` for the visible task list because those carry the full
 * task detail. The asymmetry is deliberate and not a bug to align away:
 * `taskExamples` holds no offers at all — measured across the indexable corpus
 * as 0 bids on all 4,429 pages, against 206,866 bids on `similarTasks`. Matching
 * the display order here would yield an empty bid array on every page and
 * publish no price anywhere.
 *
 * It follows that the visible list carries no bids for a visitor to compare the
 * quoted band against, so the two orders cannot contradict each other on screen.
 *
 * Bids on stored tasks are ALREADY MAJOR UNITS. Converting here is the exact
 * bug that once published `lowPrice: 2.05 DKK` on every `/hh` page — only the
 * browser-only `fetchAssociatedTasks` path returns minor units, and that path
 * does its own conversion (`minorToMajor` in `seo.component.ts`).
 */
export function bidsFromStoredTasks(source: LivePriceSource): number[] {
  const tasks =
    (source.similarTasks?.length ?? 0) > 2
      ? source.similarTasks!
      : (source.taskExamples?.length ?? 0) > 2
        ? source.taskExamples!
        : [];
  return tasks
    .filter((task) => task && task.offers && task.offers.length > 0)
    .flatMap((task) => task.offers!.map((offer) => offer?.priceBid))
    .filter(isUsable);
}

/**
 * The `live` price for a document carrying stored tasks, or null when there is
 * nothing to derive one from. The q25 low end is inherently robust to the
 * platform-minimum cluster of bids sitting at exactly the budget floor, so no
 * per-market floor constant is applied — by design.
 */
export function computeLiveDisplayPrice(
  source: LivePriceSource | null | undefined,
): DisplayPrice | null {
  if (!source) {
    return null;
  }
  const bids = bidsFromStoredTasks(source);
  if (bids.length === 0) {
    return null;
  }
  const sorted = [...bids].sort((a, b) => a - b);
  return {
    min: ceil10(quantile(sorted, 0.25)),
    middle: ceil10(quantile(sorted, 0.5)),
    max: ceil10(quantile(sorted, 0.75)),
    unit: 'task',
    origin: 'live',
    sampleSize: bids.length,
    confidence: confidenceFor(bids.length, coefficientOfVariation(bids)),
  };
}
